import React, { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import * as _ from 'lodash';
import * as XLSX from 'xlsx-js-style';
import { useNavigate, NavigateFunction } from 'react-router';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { Divider, TextField, Button, Box, Flex, Icon, Loader, Heading, Label, Dropdown, Text } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';
import { Check, Alert, Table } from '@vibe/icons';
import { OutputFormat, TemplateBasic } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';
import { ErrorBox } from '@gorilla/ui/src/components/ErrorBox';
import { useSpreadsheetLoader } from '../hooks/use-spreadsheet-loader';
import { useSpreadsheetTemplate } from '../hooks/use-spreadsheet-template';
import { createRandomId } from '../lib/utils';
import { saveSpreadsheet } from '../services/spreadsheets';
import { mondayClient } from '../services/monday';
import { getTemplates } from '../services/templates';

interface CreateSpreadsheetModalContext {
  templateId?: number;
  formatId?: string;
  spreadsheetName?: string;
  sheetId?: string;
  excelTemplateFile?: any;
  disableTemplateSelection?: boolean;
  showSelection?: boolean;
}

type Path = string[];

type FormatConfigurationProps = {
  ctx: CreateSpreadsheetModalContext;
  onCtxChange: (ctx: CreateSpreadsheetModalContext) => void;
};

async function readExcelTemplate(excelTemplateFile: any) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsBinaryString(excelTemplateFile);

    reader.onload = (e) => {
      try {
        const data = (e.target as any).result;
        const workbook = XLSX.read(data, { type: 'binary' });
        resolve(workbook);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = (err) => {
      reject(err);
    };
  });
}
function InAppConfiguration({ ctx, onCtxChange }: FormatConfigurationProps) {
  return (
    <Box>
      <TextField
        value={ctx.spreadsheetName}
        onChange={(newSpreadsheetName) => {
          onCtxChange({ ...ctx, spreadsheetName: newSpreadsheetName });
        }}
        size="large"
        placeholder="Spreadsheet name"
        autoFocus
        className="textfield--smallfont"
      />
    </Box>
  );
}

function SheetSelectConfiguration({ ctx, onCtxChange }: FormatConfigurationProps) {
  const { isLoading, template } = useSpreadsheetTemplate(ctx.templateId);

  const options = template?.config.sheets.map((sheet) => ({
    label: sheet.name,
    value: sheet.id,
  }));

  let selectedOption = options?.find((option) => option.value === ctx.sheetId);

  if (!selectedOption && options?.length) {
    selectedOption = options[0];
  }

  return (
    <Box>
      <>
        <Dropdown
          searchable
          disabled={!ctx.templateId}
          loading={isLoading}
          onOptionSelect={(ev: any) => {
            onCtxChange({ ...ctx, sheetId: ev.value });
          }}
          value={dropdownValue(selectedOption)}
          options={options ?? []}
          placeholder="Select sheet"
          clearable={false}
          size="large"
        />
      </>
    </Box>
  );
}

type Format = {
  id: string;
  label: string;
  description: string;
  hint: string;
  info: string;
  outputFormat: string;
  configurationComponent: React.ComponentType<FormatConfigurationProps> | null;
  creationHandler: (ctx: CreateSpreadsheetModalContext, data: any, navigate: NavigateFunction) => Promise<void>;
  isConfigurationValid?: (ctx: CreateSpreadsheetModalContext) => boolean;
  createButtonLabel: string;
};

const formats: Format[] = [
  {
    id: 'excel',
    label: 'Export Excel',
    description: 'and download the file',
    hint: '',
    info: '',
    outputFormat: 'sheetjs',
    configurationComponent: null,
    creationHandler: async (ctx: CreateSpreadsheetModalContext, data: any) => {
      let templateWorkbook: any = null;

      if (ctx.excelTemplateFile) {
        try {
          templateWorkbook = await readExcelTemplate(ctx.excelTemplateFile);
        } catch (err) {
          console.log('failed to load excel template', err);
        }
      }

      if (templateWorkbook) {
        Object.assign(templateWorkbook.Sheets, data.Sheets);
        templateWorkbook.SheetNames = _.uniq([...templateWorkbook.SheetNames, ...data.SheetNames]);
        XLSX.writeFile(templateWorkbook, 'out.xlsx');
      } else {
        XLSX.writeFile(data, 'out.xlsx');
      }
    },
    createButtonLabel: 'Download',
  },
  {
    id: 'csv',
    label: 'Export CSV',
    description: 'and download the file',
    hint: '',
    info: '',
    outputFormat: 'sheetjs',
    configurationComponent: SheetSelectConfiguration,
    creationHandler: async (ctx: CreateSpreadsheetModalContext, data: any) => {
      let sheetId = ctx.sheetId || data.Sheets[data.SheetNames[0]]['!sheetId'];
      const sheet = Object.values(data.Sheets).find((sheet) => (sheet as any)['!sheetId'] === sheetId);
      const sheetName = (sheet as any)['!sheetName'];
      XLSX.writeFile(data, `${sheetName}.csv`, { sheet: sheetName });
    },
    createButtonLabel: 'Download',
  },
];

type SummaryStepProps = {
  templates: TemplateBasic[];
  onClose: () => void;
  ctx: CreateSpreadsheetModalContext;
  onCtxChange: (ctx: CreateSpreadsheetModalContext) => void;
  onPathChange: (path: Path) => void;
  path: Path;
};

function SummaryStep({ ctx, path, onClose, onCtxChange, onPathChange, templates }: SummaryStepProps) {
  const navigate = useNavigate();
  const template = templates.find((t) => t.id === ctx.templateId);
  const format = formats.find((format) => format.id === ctx.formatId);
  const { loadingMessage, data, error } = useSpreadsheetLoader(ctx ? ctx.templateId : undefined, format?.outputFormat as OutputFormat);

  const { trigger: createSpreadsheetTrigger, isMutating: isCreatingSpreadsheet } = useSWRMutation('/api/create-spreadsheet', async () => {
    if (format && format.creationHandler) {
      try {
        return await format.creationHandler(ctx, data, navigate);
      } catch (err) {
        enqueueSnackbar('Failed to create spreadsheet: ' + _.get(err, 'message', ''), {
          variant: 'error',
        });
        throw err;
      }
    }
  });

  let statusIcon = <></>;

  if (error) {
    statusIcon = (
      <div className="statusChecked error">
        <Icon icon={Alert} size={10} />
      </div>
    );
  } else if (data) {
    statusIcon = (
      <div className="statusChecked">
        <Icon icon={Check} size={10} />
      </div>
    );
  } else {
    statusIcon = <Loader size="xs" color="primary" />;
  }

  return (
    <>
      <AppModalContent>
        <Box border={true} borderColor="layoutBorderColor" rounded="small">
          <>
            {format && format.id === 'in-app' ? (
              <>
                <Box padding="medium">
                  <Flex gap={8}>
                    <div>
                      <strong>Name:</strong> {ctx.spreadsheetName}
                    </div>
                    <div className="statusChecked">
                      <Icon icon={Check} size={10} />
                    </div>
                  </Flex>
                </Box>
                <Divider withoutMargin={true} />
              </>
            ) : (
              <></>
            )}
            <Box padding="medium">
              <Flex gap={8}>
                <div style={{ fontSize: '14px' }}>
                  <strong>Template:</strong> {template?.name}
                </div>
                <div className="statusChecked">
                  <Icon icon={Check} size={10} />
                </div>
              </Flex>
            </Box>
            <Divider withoutMargin={true} />
            <Box padding="medium">
              <Flex gap={8}>
                <div style={{ fontSize: '14px' }}>
                  <strong>Items:</strong> {error ? 'Loading failed' : loadingMessage}
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <Flex gap={8}>{statusIcon}</Flex>
                </div>
              </Flex>
            </Box>
          </>
          {error ? <ErrorBox error={error} mondayClient={mondayClient} /> : null}
        </Box>
      </AppModalContent>
      <AppModalFooter>
        <Flex>
          <Box style={{ marginLeft: 'auto' }}>
            <Flex gap={12} justify="end">
              <Button
                kind="tertiary"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                kind="secondary"
                onClick={() => {
                  onPathChange(['configuration']);
                }}
              >
                Prev
              </Button>
              <Button
                disabled={!data}
                loading={isCreatingSpreadsheet}
                onClick={async () => {
                  await createSpreadsheetTrigger();
                  onClose();
                }}
              >
                {format?.createButtonLabel || 'Create'}
              </Button>
            </Flex>
          </Box>
        </Flex>
      </AppModalFooter>
    </>
  );
}

type ConfigurationStepProps = {
  onClose: () => void;
  ctx: CreateSpreadsheetModalContext;
  onCtxChange: (ctx: CreateSpreadsheetModalContext) => void;
  onPathChange: (path: Path) => void;
  path: Path;
  templates: TemplateBasic[];
  showPrevButton: boolean;
};

function ConfigurationStep({ ctx, path, onClose, onCtxChange, onPathChange, templates, showPrevButton }: ConfigurationStepProps) {
  const templateOptions: { label: string; value: number }[] = _.orderBy(templates, 'name').map((template) => ({
    label: template.name,
    value: template.id,
  }));

  const format = formats.find((format) => format.id === ctx.formatId);
  const selectedTemplateOption = templateOptions.find((option) => option.value === ctx.templateId);
  const FormatConfiguration = format && format.configurationComponent ? format.configurationComponent : null;
  const formatConfiguration = FormatConfiguration ? <FormatConfiguration ctx={ctx} onCtxChange={onCtxChange} /> : null;

  return (
    <>
      <AppModalContent>
        {format && format.hint !== '' ? (
          <Box marginBottom="medium">
            <>{format.hint}</>
          </Box>
        ) : (
          <></>
        )}
        <Box>
          <Dropdown
            searchable
            disabled={!!ctx.disableTemplateSelection}
            onOptionSelect={(ev: any) => {
              onCtxChange({ ...ctx, templateId: ev.value, sheetId: undefined });
            }}
            options={templateOptions}
            placeholder="Select template"
            value={dropdownValue(selectedTemplateOption)}
            clearable={false}
            size="large"
          />
        </Box>
        {formatConfiguration ? <div style={{ marginTop: '16px' }}>{formatConfiguration}</div> : <></>}
      </AppModalContent>
      <AppModalFooter>
        <Flex>
          <div style={{ color: '#afb0bd' }}>
            {format?.label || ''} {format?.description || ''}
          </div>
          <Box style={{ marginLeft: 'auto' }}>
            <Flex gap={12} justify="end">
              <Button
                kind="tertiary"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <>
                {showPrevButton ? (
                  <Button
                    kind="secondary"
                    onClick={() => {
                      onPathChange([]);
                    }}
                  >
                    Prev
                  </Button>
                ) : null}
              </>

              <Button
                disabled={!ctx.templateId || (format && format.isConfigurationValid && !format.isConfigurationValid(ctx))}
                onClick={() => {
                  onPathChange(['configuration', 'summary']);
                }}
              >
                Next
              </Button>
            </Flex>
          </Box>
        </Flex>
      </AppModalFooter>
    </>
  );
}

type FormatSelectionStepProps = {
  onClose: () => void;
  ctx: CreateSpreadsheetModalContext;
  onCtxChange: (ctx: CreateSpreadsheetModalContext) => void;
  onPathChange: (path: Path) => void;
  path: Path;
};

function FormatSelectionStep({ ctx, path, onClose, onCtxChange, onPathChange }: FormatSelectionStepProps) {
  return (
    <>
      <AppModalContent>
        <Box>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'stretch',
              justifyContent: 'stretch',
              gap: '24px',
            }}
          >
            {formats.map((format, i) => (
              <div
                key={format.id}
                className={ctx.formatId === format.id ? 'multiTypeSelected multiType' : 'multiType'}
                onClick={() => {
                  onCtxChange({ ...ctx, formatId: format.id });
                }}
              >
                <div className="checkTypeIcon">
                  <Icon icon={Check} size={16} />
                </div>
                <Text type="text2" weight="medium" align="center" ellipsis={false}>
                  {format.label}
                </Text>
                {/* 
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 300,
                      color: '#676879',
                    }}
                  >
                    {format.description}
                  </div>
                  */}
              </div>
            ))}
          </div>
        </Box>
      </AppModalContent>
      <AppModalFooter>
        <Flex>
          <div style={{ color: '#afb0bd' }}>Choose from the options above and proceed</div>
          <Box style={{ marginLeft: 'auto' }}>
            <Flex gap={12} justify="end">
              <Button
                kind="tertiary"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!ctx.formatId}
                onClick={() => {
                  onPathChange(['configuration']);
                }}
              >
                Next
              </Button>
            </Flex>
          </Box>
        </Flex>
      </AppModalFooter>
    </>
  );
}

type CreateSpreadsheetModalProps = {
  onClose: () => void;
  show: boolean;
  initialCtx?: CreateSpreadsheetModalContext;
};

export function CreateSpreadsheetModal({ show, onClose, initialCtx }: CreateSpreadsheetModalProps) {
  const [path, setPath] = useState<Path>([
    ...(_.get(initialCtx, 'formatId') && !_.get(initialCtx, 'showSelection') ? ['configuration'] : []),
  ]);
  const [ctx, setCtx] = useState<CreateSpreadsheetModalContext>(initialCtx || {});
  const { data: templates } = useSWR('/api/get-templates', async () => getTemplates());

  let modalContent = <></>;

  if (!templates) {
    modalContent = <>Loading...</>;
  } else if (path.length === 0) {
    modalContent = (
      <FormatSelectionStep
        onClose={onClose}
        ctx={ctx}
        onCtxChange={(ctx) => setCtx(ctx)}
        onPathChange={(path) => setPath(path)}
        path={path}
      />
    );
  } else if (path.length === 1) {
    modalContent = (
      <ConfigurationStep
        templates={templates}
        onClose={onClose}
        ctx={ctx}
        onCtxChange={(ctx) => setCtx(ctx)}
        onPathChange={(path) => setPath(path)}
        path={path}
        showPrevButton={!_.get(initialCtx, 'formatId')}
      />
    );
  } else if (path.length === 2) {
    modalContent = (
      <SummaryStep
        onClose={onClose}
        ctx={ctx}
        onCtxChange={(ctx) => setCtx(ctx)}
        onPathChange={(path) => setPath(path)}
        path={path}
        templates={templates}
      />
    );
  }

  return (
    <AppModal show={show} onClose={() => onClose()} width="default" alertDialog={false} data-testid="create-spreadsheet-modal">
      <Flex direction="row" align="center" gap={4} style={{ marginBottom: '24px' }}>
        <Flex gap={8}>
          <Icon icon={Table} size={20} />
          <div style={{ maxWidth: '200px' }}>
            <Text type="text1" weight="medium" ellipsis>
              Export spreadsheet
            </Text>
          </div>
        </Flex>
      </Flex>
      <div>{modalContent}</div>
    </AppModal>
  );
}
